import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment/moment'

import find from 'lodash/find'
import join from 'lodash/join'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import toUpper from 'lodash/toUpper'
import uniqueId from 'lodash/uniqueId'
import upperFirst from 'lodash/upperFirst'

import {
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core'
import {
  DoneAll as DoneAllIcon,
  Info as InfoIcon
} from '@material-ui/icons'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import {
  moreVertical as moreVerticalIcon
} from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import ActivityConstants from '@galo-monitor/stores/activity/activityConstants'
import { ActivityActions } from '@galo-monitor/stores/activity/duckActivity'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import colors from '@galo-monitor/styles/colors'
import { momentFriendlyDateFormat, momentFriendlyDateUSFormat } from '@galo-monitor/utils/dates'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import IconButton from '@galo-monitor/web-components/IconButton'
import Popover from '@galo-monitor/web-components/Popover'
import Screen from '@galo-monitor/web-components/Screen'
import Tooltip from '@galo-monitor/web-components/Tooltip'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ActivityModal from '@galo-monitor/web-containers/modals/property/ActivityModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import HarvestSelector from './components/HarvestSelector'
import PlotListSelector from './components/PlotListSelector'
import PopoverAddButton from './components/PopoverAddButton'
import { useActivities } from './context/Activities'
import useStyles from './styles'
import {
  TextBold
} from './theme'

const ACTIVITY_ACTIONS = ['edit', 'delete']
const ACTIVITY_TYPES = [
  ActivityConstants.SOIL_PREPARATION_ACTIVITY_TYPE,
  ActivityConstants.SEEDING_ACTIVITY_TYPE,
  ActivityConstants.FERTILIZATION_ACTIVITY_TYPE,
  ActivityConstants.PESTICIDE_APPLICATION_ACTIVITY_TYPE,
  ActivityConstants.HARVEST_ACTIVITY_TYPE
]

const CENTER_CELL_STYLE = {
  headerStyle: {
    textAlign: 'center'
  },
  cellStyle: {
    textAlign: 'center'
  }
}

const FERTILIZATION_REGEX = /fertil/i

const ActivitiesScreen = () => {
  const classes = useStyles()
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const currentProperty = useSelector(selectCurrentProperty)
  const { selected } = useSelector(selectLanguage)
  const { currentPlotId, currentHarvest } = useActivities()

  const multipleActionsPopoverRef = useRef(null)

  const {
    data: harvests,
    mutate: mutateHarvests
  } = useGaloMonitorApi('/farms/properties/activities/harvests')

  const {
    data: harvestData,
    isValidating: isValidatingHarvestData,
    mutate: mutateHarvestData
  } = useGaloMonitorApi(`/farms/properties/activities/plots/${ currentHarvest.value }/harvests`)

  const {
    data: tableData,
    isValidating: isValidatingTableData,
    mutate: mutateTableData
  } = useGaloMonitorApi(`/farms/properties/activities/list/${ currentHarvest.value }?page=1&plot_id=${ currentPlotId }`)

  const userActions = useMemo(
    () => getActionsBySlug(routes.activities.slug),
    [getActionsBySlug, routes.activities.slug]
  )

  const harvestOptions = useMemo(() => map(harvests, harvest => (
    {
      label: harvest.name,
      value: String(harvest.id)
    }
  )), [harvests])

  const findActivityById = useCallback(harvestId => find(harvests, ({ id }) => harvestId === String(id)), [harvests])

  const onAddClick = useCallback(
    activityType => {
      createDialog({
        id: 'create-activity',
        Component: ActivityModal,
        props: {
          activity: {
            type: activityType
          },
          classes,
          currentHarvest: findActivityById(currentHarvest.value),
          plots: harvestData?.plots,
          onSuccess: mutateTableData
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, createDialog, currentHarvest, isValidatingHarvestData, mutateTableData]
  )

  const onEditClick = useCallback(
    activity => {
      createDialog({
        id: 'edit-activity',
        Component: ActivityModal,
        props: {
          activity: {
            ...activity,
            type: activity.type.match(FERTILIZATION_REGEX)
              ? ActivityConstants.FERTILIZATION_ACTIVITY_TYPE
              : activity.type
          },
          classes,
          currentHarvest: findActivityById(currentHarvest.value),
          plots: harvestData?.plots,
          onSuccess: mutateTableData
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, createDialog, currentHarvest, isValidatingHarvestData, mutateTableData]
  )

  const onDeleteClick = useCallback(
    activity => {
      createDialog({
        id: 'delete-activity',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('activity', { howMany: 1 }),
            name: `${ activity.type.match(FERTILIZATION_REGEX) ? activity.type : t(`${ activity.type } activity type`) } -
              ${ t(`${ activity.status }`, { howMany: 1 }) } -
              ${ activity.userResponsible }`
          }),
          onConfirm: () => {
            dispatch(ActivityActions.removeActivity(
              {
                ...activity,
                type: activity.type.match(FERTILIZATION_REGEX)
                  ? ActivityConstants.FERTILIZATION_ACTIVITY_TYPE
                  : activity.type
              },
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('activity', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutateTableData()
              },
              () => {
                createDialog({
                  id: 'activities-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('delete')),
                      this: toLower(t('activity', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutateTableData, snackbar, t]
  )

  const onActivityDoneClick = useCallback(
    activity => {
      createDialog({
        id: 'create-done-activity',
        Component: ActivityModal,
        props: {
          activity: {
            ...activity,
            type: activity.type.match(FERTILIZATION_REGEX)
              ? ActivityConstants.FERTILIZATION_ACTIVITY_TYPE
              : activity.type
          },
          activityState: 'realized',
          classes,
          currentHarvest,
          onSuccess: mutateTableData
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, createDialog, currentHarvest, mutateTableData]
  )

  const activityActions = useMemo(() => ({
    edit: onEditClick,
    delete: onDeleteClick
  }), [onDeleteClick, onEditClick])

  const onActionClick = useCallback((action, activity) => {
    activityActions[action](activity)
    // eslint-disable-next-line no-unused-expressions
    multipleActionsPopoverRef?.current?.onClose()
  }, [activityActions])

  const columns = useMemo(
    () => [
      {
        title: t('activity', { howMany: 1 }),
        field: 'type',
        render: rowData => (
          <TextBold variant="body1">
            {
              ACTIVITY_TYPES.includes(rowData.type)
                ? t(`${ rowData.type } listing label`)
                : rowData.type
            }
          </TextBold>
        )
      },
      {
        title: t('status'),
        field: 'status',
        render: rowData => (
          <Chip
            className={
              join([
                classes.chip,
                classes.fontWhite,
                // eslint-disable-next-line no-nested-ternary
                rowData.status === 'realized'
                  ? classes.success
                  : (
                    rowData.status === 'late'
                      ? classes.error
                      : classes.default
                  )
              ], ' ')
            }
            label={ t(rowData.status, { howMany: 1 }) }
          />
        )
      },
      {
        title: t('start date'),
        field: 'date_initial',
        sorting: false,
        render: ({ dateInitial }) => moment(dateInitial)
          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
      },
      {
        title: t('end date'),
        field: 'date_finish_planned',
        sorting: false,
        render: ({ dateFinishPlanned }) =>
          dateFinishPlanned
            ? moment(dateFinishPlanned).format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
            : '-'
      },
      {
        title: t('concluded', { howMany: 1 }),
        field: 'date_finish_realized',
        sorting: false,
        render: ({ dateFinishRealized }) =>
          dateFinishRealized
            ? moment(dateFinishRealized).format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
            : '-'
      },
      {
        title: t('collaborator', { howMany: 1 }),
        field: 'user_responsible',
        sorting: false,
        ...CENTER_CELL_STYLE,
        render: rowData => (
          <Tooltip
            className={ classes.center }
            title={ rowData.userResponsible }
          >
            <Chip
              className={ join([
                classes.chip,
                classes.fontBlack,
                classes.collaborator
              ], ' ') }
              label={ rowData.userResponsible[0] }
            />
          </Tooltip>
        )
      },
      {
        title: t('plot', { howMany: 2 }),
        field: 'plots',
        sorting: false,
        render: ({ plots }) => join(map(plots, ({ name }) => name), ', ')
      },
      {
        title: toUpper(t('observation abbreviation')),
        field: 'observation',
        sorting: false,
        ...CENTER_CELL_STYLE,
        render: () => (
          <IconButton
            className={ classes.disabled }
            size="small"
            disabled
          >
            <InfoIcon color="action" />
          </IconButton>
        )
      },
      {
        title: t('done', { howMany: 1 }),
        field: 'done',
        sorting: false,
        ...CENTER_CELL_STYLE,
        render: rowData => (
          <IconButton
            onClick={ () => onActivityDoneClick(rowData) }
            size="small"
            disabled={ rowData.status === 'realized' }
          >
            <DoneAllIcon color={ rowData.status === 'realized' ? 'primary' : 'action' } />
          </IconButton>
        )
      },
      {
        render: rowData => (
          <div className={ classes.center }>
            <Popover
              ref={ multipleActionsPopoverRef }
              popoverId={ uniqueId('popover-actions') }
              Component={ props => (
                <IconButton
                  tooltip={ t('action', { howMany: 2 }) }
                  size="small"
                  { ...props }
                >
                  <Icon icon={ moreVerticalIcon } color={ colors.darkGrey } />
                </IconButton>
              ) }
            >
              <List style={ { padding: 0 } }>
                {map(ACTIVITY_ACTIONS, action => userActions[action] ? (
                  <React.Fragment key={ uniqueId(action) }>
                    <ListItem
                      className={ join([classes.item, classes.actionItem], ' ') }
                      onClick={ () => onActionClick(action, rowData) }
                      button
                    >
                      <ListItemText primary={ upperFirst(t(action === 'delete' ? 'remove' : action)) } />
                    </ListItem>
                    <Divider style={ { margin: 0 } } />
                  </React.Fragment>
                ) : null)}
              </List>
            </Popover>
          </div>
        )
      }
    ],
    [classes.actionItem, classes.center, classes.chip, classes.collaborator, classes.default, classes.disabled, classes.error, classes.fontBlack, classes.fontWhite, classes.item, classes.success, onActionClick, onActivityDoneClick, selected, t, userActions]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.activities.title,
      key: 'farm-property-activities-screen',
      url: '/activities',
      active: true
    }
  ], [routes.activities.title])

  useEffect(() => {
    mutateHarvests()
    mutateHarvestData()
    mutateTableData()
  }, [currentProperty, mutateHarvestData, mutateHarvests, mutateTableData])

  return (
    <Screen
      title={ routes.activities.title }
      breadcrumbs={ crumbs }
      headerChildren={
        <HarvestSelector
          label={ t('harvest', { howMany: 1 }) }
          options={ harvestOptions }
        />
      }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 12 } md={ 3 }>
          <PlotListSelector
            harvestData={ harvestData }
            loading={ isValidatingHarvestData }
          />
        </Grid>
        <Grid
          className={ classes.overflowTableContainer }
          item
          xs={ 12 }
          md
        >
          <DataTable
            loading={ isValidatingTableData }
            data={ tableData?.results ?? [] }
            columns={ columns }
            onAddClick={ userActions.write && onAddClick }
            CustomAddButton={ (
              <PopoverAddButton
                ref={ multipleActionsPopoverRef }
                t={ t }
                classes={ classes }
                list={ ACTIVITY_TYPES }
                onClick={ onAddClick }
                disabled={ isValidatingTableData || currentHarvest.value === '0' }
              />
            ) }
          />
        </Grid>
      </Grid>
    </Screen>
  )
}

export default ActivitiesScreen
